import React, { useState, useEffect } from 'react';
import {
  getRehydratableName,
  getClassNameFactory,
  Button,
  Responsive,
  Heading,
  Sizes,
  Typography,
  FlexGrid,
} from '@dcl/ui/';
import './index.css';
import { IModuleListProps, IModuleData } from './types';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import { ButtonColor, ButtonKind } from '@dcl/ui/dist/elements/Button';
import SubModule from './components/SubModule/SubModule';
import { DESKTOP_WIDTH } from '../UltraTaxCS/constants';
import { MOBILE_WIDTH } from '../TermsAndConditions/constants';
import MobileView from './components/MobileView';
import { convertDataToDropdownList } from './utils';
import './ModuleList.css';
import { SubModuleRedesigned } from './components/SubModuleRedesigned/SubModuleRedesigned';

export const ModuleList: React.FC<IModuleListProps> = (props) => {
  const {
    modulesData,
    showLessButtonLabel,
    showMoreButtonLabel,
    chooseASubjectLabel,
    goToSubjectLabel,
    variation,
    title,
    description,
  } = props;
  const getClassName = getClassNameFactory(ModuleList.displayName);
  const [isExpanded, setIsExpanded] = useState(false);
  const itemsToShow = 5;
  const [variation2ItemsToShow, setVariation2ItemsToShow] = useState(4);
  const MD_MAX_WIDTH = 991;

  useEffect(() => {
    const calculateItemsToShow = () => {
      const width = window.innerWidth;
      if (width <= MD_MAX_WIDTH) {
        setVariation2ItemsToShow(3);
      } else {
        setVariation2ItemsToShow(4);
      }
    };
    calculateItemsToShow();
    window.addEventListener('resize', calculateItemsToShow);
    return () => window.removeEventListener('resize', calculateItemsToShow);
  }, []);

  const onShowMoreClick = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      className={getClassName()}
      data-rehydratable={getRehydratableName(ModuleList.displayName)}
      {...propsToDataAttributes(props)}
    >
      {variation && variation === 'variation-2' ? (
        <>
          <FlexGrid fullWidth>
            <FlexGrid.Row
              className={getClassName({ descendantName: 'flexRow' })}
            >
              <FlexGrid.Col
                md={12}
                lg={12}
                sm={12}
                className={getClassName({ descendantName: 'columnOne' })}
              >
                <Heading
                  type={Sizes.LARGE}
                  className={getClassName({
                    descendantName: 'variation2title',
                  })}
                >
                  <strong>{title}</strong>
                </Heading>
              </FlexGrid.Col>
              <FlexGrid.Col
                md={12}
                lg={12}
                sm={12}
                className={getClassName({ descendantName: 'columnOne' })}
              >
                <Typography
                  type={Sizes.MEDIUM}
                  className={getClassName({
                    descendantName: 'variation2description',
                  })}
                >
                  {description}
                </Typography>
              </FlexGrid.Col>
            </FlexGrid.Row>
            <FlexGrid.Row
              className={getClassName({ descendantName: 'variation2' })}
            >
              {modulesData
                ?.slice(
                  0,
                  isExpanded ? modulesData.length : variation2ItemsToShow
                )
                .map((moduleDataItem: IModuleData, index: number) => {
                  const keyValue = `${moduleDataItem.url}${index + 1}`;
                  return (
                    <FlexGrid.Col
                      md={4}
                      lg={3}
                      sm={12}
                      key={keyValue}
                      className={getClassName({ descendantName: 'listCol' })}
                    >
                      <SubModuleRedesigned
                        text={moduleDataItem.title}
                        url={moduleDataItem.url}
                        newTab={moduleDataItem.newTab}
                      ></SubModuleRedesigned>
                    </FlexGrid.Col>
                  );
                })}
            </FlexGrid.Row>
          </FlexGrid>
          {modulesData.length > variation2ItemsToShow && (
            <div
              className={getClassName({
                descendantName: 'showMoreButtonContainer',
              })}
            >
              <Button
                kind={ButtonKind.BUTTON}
                color={ButtonColor.TERTIARY}
                shape="circular"
                onClick={() => onShowMoreClick()}
              >
                {isExpanded ? showLessButtonLabel : showMoreButtonLabel}
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <Responsive minWidth={DESKTOP_WIDTH}>
            <div className={getClassName({ descendantName: 'container' })}>
              {modulesData
                ?.slice(0, isExpanded ? modulesData.length : itemsToShow)
                .map((moduleDataItem: IModuleData, index: number) => {
                  const keyValue = `${moduleDataItem.url}${index + 1}`;
                  return (
                    <SubModule
                      key={keyValue}
                      text={moduleDataItem.title}
                      url={moduleDataItem.url}
                      newTab={moduleDataItem.newTab}
                    ></SubModule>
                  );
                })}
            </div>
            {modulesData.length > 5 ? (
              <div
                className={getClassName({ descendantName: 'buttonContainer' })}
              >
                <Button
                  kind={ButtonKind.BUTTON}
                  color={ButtonColor.SECONDARY}
                  shape="circular"
                  onClick={() => onShowMoreClick()}
                >
                  {isExpanded ? showLessButtonLabel : showMoreButtonLabel}
                </Button>
              </div>
            ) : (
              <></>
            )}
          </Responsive>

          <Responsive minWidth={0} maxWidth={MOBILE_WIDTH}>
            {modulesData.length ? (
              <MobileView
                modulesData={convertDataToDropdownList(modulesData)}
                chooseASubjectLabel={chooseASubjectLabel}
                goToSubjectLabel={goToSubjectLabel}
              ></MobileView>
            ) : (
              <></>
            )}
          </Responsive>
        </>
      )}
    </div>
  );
};

ModuleList.displayName = 'ModuleList';
